
import React, { createContext, useEffect, useRef, useState, useMemo } from 'react'
import firebase from 'firebase/compat/app'
import { isUserAdmin, isUserDelivery, isUserChef, isUserDev, getUsers, getUser, updateDeliveryLocation } from '../firebase/obj/user'
import { getClientData, getClientsRealTime } from '../firebase/obj/client'
import { getConfig, getConfigRealTime } from '../firebase/obj/config'
import { checkTermsAndConditions } from '../utils'
import { getPaymentMethods } from '../firebase/obj/paymentMethods'
import { getBusinessInfo } from '../firebase/obj/businessInfo'
import { getOrderStates, sortSates } from '../firebase/obj/orderState'
import { getProductsCategory, getProductsRealTime } from '../firebase/obj/product'
import { getStaff } from '../firebase/obj/staff'
import { getActiveGroups, getGroupsRealTime } from '../firebase/obj/productGroup'
import { beepersRealTime } from '../firebase/obj/beepers'
import { getModules } from '../firebase/obj/modules'
import { useLocation } from '@reach/router';
import { getRolesFiltered } from '../firebase/obj/roles'
import useOptimizedProductSearch from '../hooks/useOptimizedProductSearch'
import { Timestamp } from '@firebase/firestore';

export const Context = createContext()


const Provider = ({ children }) => {
  const [isAuth, setIsAuth] = useState(() => {
    return window.sessionStorage.getItem('user')
  })
  const [modules, setModules] = useState({})
  const [user, setUser] = useState(null)
  const [client, setClient] = useState(null)
  const [isAdmin, setAdmin] = useState(false)
  const [isDev, setDev] = useState(false)
  const [isDelivery, setDelivery] = useState(false)
  const [isChef, setChef] = useState(false)
  const [clients, setClients] = useState([])
  const [config, setConfig] = useState({})
  const [cart, setCart] = useState({ productsList: [], totalQty: 0, pickup: false })
  const [paymentMethods, setPaymentMethods] = useState([])
  const [businessInfo, setBusinessInfo] = useState({})
  const [orderStates, setOrderStates] = useState([])
  const [products, setProducts] = useState([])
  const [staffs, setStaffs] = useState([])
  const [productGroups, setPorductGroups] = useState([])
  const [productsCategories, setProductsCategory] = useState([])
  const [beepers, setBeepers] = useState([])
  const [search, setSearch] = useState('')
  const [showFooter, setShowFooter] = useState(true)
  const layoutContainerRef = useRef()
  const location = useLocation();

  useEffect(() => {
    if (user) {
      loadPaymentMethods()
      fetchOrderStates()
    }
  }, [user])

  useEffect(() => {
    if (isDelivery && user?.email) {
      const dbRequest = indexedDB.open('piopio-db', 1);

      dbRequest.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('app-state')) {
          db.createObjectStore('app-state');
        }
      };

      dbRequest.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['app-state'], 'readwrite');
        const objectStore = transaction.objectStore('app-state');
        objectStore.put(user.email, 'userEmail');
        console.log('Delivery user email saved to IndexedDB.');
      };

      dbRequest.onerror = (event) => {
        console.error('IndexedDB error:', event.target.errorCode);
      };
    }
  }, [isDelivery, user]);

  useEffect(() => {
    let locationInterval;

    const sendLocation = (position) => {
      try {
        const { latitude, longitude } = position.coords;
        const email = firebase.auth().currentUser?.email;

        if (email) {
          updateDeliveryLocation(email, { latitude, longitude, timestamp: new Date() })
        }
      } catch (error) {
        console.error('sendLocation', error)
      }
    };

    const watchPosition = () => {
      try {
        navigator.geolocation.getCurrentPosition(sendLocation, null, {
          enableHighAccuracy: true,
        });
      } catch (error) {
        console.error('watchPosition', error)
      }
    };

    if (isDelivery && modules?.deliveryTracking) {
      watchPosition(); // Send initial position
      locationInterval = setInterval(watchPosition, 30000); // Send position every 30 seconds
    }

    return () => {
      if (locationInterval) {
        clearInterval(locationInterval);
      }
    };
  }, [isDelivery, modules?.deliveryTracking]);

  useEffect(() => {
    if (location.pathname === '/orders' && isDelivery) {
      setShowFooter(false)

    } else {
      setShowFooter(true)
    }
  }, [isDelivery, location])


  const productsFiltered = useOptimizedProductSearch(products, search);

  const loadUserPermissions = async (user) => {
    let permissions = []
    try {
      if (!user)
        return permissions

      const roles = await getRolesFiltered(user.rolesIds)
      permissions = roles?.map(r => r.permissions).flat() || []

    } catch (error) {
      console.error('loadUserRoles', error)
    }
    return permissions
  }

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (authUser) => {
      if (authUser != null) {

        const userAdmin = await getUser(authUser)
        const permissions = userAdmin?.id ? await loadUserPermissions(userAdmin) : []

        const clientData = await getClientData(authUser.uid)
        setClient(clientData)

        const userData = {
          ...clientData,
          ...userAdmin,
          uid: authUser.uid,
          email: authUser.email,
          displayName: authUser.displayName,
          photoURL: authUser.photoURL,
          phoneNumber: authUser.phoneNumber,
          permissions
        }
        setUser(userData)

        setIsAuth(true)
        setAdmin(userData?.admin)
        setDelivery(userData?.delivery)
        setChef(userData?.chef)
        if (clientData?.termsConditions) {
          window.sessionStorage.setItem(`termsConditions-${authUser.uid}`, `true`)
        } else {
          clientData && checkTermsAndConditions(userData)
        }
        window.sessionStorage.setItem('user', authUser.displayName)

      } else {
        setIsAuth(false)
        setAdmin(false)
        setDelivery(false)
        setChef(false)
        window.sessionStorage.setItem('user', null)
      }
    });

    function fetchData() {
      try {
        getConfigRealTime(setConfig)
        getBusinessInfo().then(info => setBusinessInfo(info))
        getProductsRealTime(setProducts)
      } catch (error) {
        console.error('fetchData', error)
      }
    }

    fetchData();

    return () => {
      unsubscribe();
      fetchData();
    }
  }, [])

  useEffect(() => {
    getModules().then(mods => setModules(mods))
  }, [])

  useEffect(() => {
    const loadData = () => {
      let cleanupFunction = () => { }
      if (isAdmin) {
        const _setState = data => {
          setClients(data)
        }
        getUsers().then(staffs => setStaffs(staffs))
        getProductsCategory().then(categories => setProductsCategory(categories))
        const _getGroupsRealTime = getGroupsRealTime(setPorductGroups)
        const _getClientsRealTime = getClientsRealTime(_setState)
        const _beepersRealTime = beepersRealTime(setBeepers)
        cleanupFunction = () => {
          if (_getGroupsRealTime) {
            _getGroupsRealTime()
          }
          if (_getClientsRealTime) {
            _getClientsRealTime()
          }
          if (_beepersRealTime) {
            _beepersRealTime()
          }
        };
      } else {
        getActiveGroups().then(groups => setPorductGroups(groups))
      }
      return cleanupFunction
    }
    return loadData()
  }, [isAdmin])


  const fetchOrderStates = () => getOrderStates().then(orderStates => {
    let states = orderStates.sort(sortSates)
    if (isDelivery) {
      states = states.filter(s => s.value >= 60)
    }
    setOrderStates(states)
  })

  const loadPaymentMethods = () =>
    getPaymentMethods().then(data => {
      setPaymentMethods(data)
    })


  const addToCart = (item) => {
    let newItem = true
    let totalQty = 0
    const productsList = cart.productsList?.map(prod => {
      if (item.id === prod.id) {
        prod.qty = item.qty
        newItem = false
      }
      totalQty += prod.qty
      return prod
    }).filter(p => Number(p.qty) > 0)

    if (newItem) {
      totalQty += item.qty
      if (Number(item.qty) > 0)
        productsList?.push(item)
    }

    setCart({ ...cart, productsList, totalQty })
  }

  const getPaymentMethodName = ({ order, previousText = 'Método de pago: ' }) => {
    if (!paymentMethods || !order.paymentMethod)
      return null

    const name = paymentMethods.find(p => p.method === order.paymentMethod)?.name
    return previousText + name
  }

  const value = {
    layoutContainerRef,
    modules,
    isAuth,
    isDev,
    user,
    client,
    isAdmin,
    isDelivery,
    isChef,
    clients,
    config,
    cart,
    paymentMethods,
    businessInfo,
    orderStates,
    products,
    productsFiltered,
    productsCategories,
    staffs,
    productGroups,
    beepers,
    search,
    showFooter,
    setShowFooter,
    setSearch,
    addToCart: addToCart,
    setCart,
    getPaymentMethodName,
    online: navigator.onLine,
    setUser,
    activateAuth: () => { },
  }

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  )
}

const context = {
  Provider,
  Consumer: Context.Consumer
}

export default context
